import React, {FC, useState} from 'react';
import {
  Avatar,
  AvatarItem,
  AvatarStack,
  Badge,
  Brand,
  Button,
  Checkbox,
  Divider,
  EmptyState,
  Eyebrow,
  Field,
  Heading,
  IconButton,
  Input,
  Motif,
  Notice,
  Paper,
  PledgeControl,
  ProgressBar,
  ProgressRing,
  SegmentedControl,
  Select,
  Spinner,
  Text,
  Textarea,
  WishCard,
} from '../ui';
import './ui-kit.css';

const avatars: AvatarItem[] = [
  {name: 'Julien', src: '/images/souhait/avatars/avatar-01.webp'},
  {name: 'Lia', src: '/images/souhait/avatars/avatar-02.webp'},
  {name: 'Camille', src: '/images/souhait/avatars/avatar-03.webp'},
  {name: 'Thomas', src: '/images/souhait/avatars/avatar-04.webp'},
  {name: 'Maya', src: '/images/souhait/avatars/avatar-05.webp'},
  {name: 'Noah', src: '/images/souhait/avatars/avatar-06.webp'},
];

const Swatch: FC<{name: string; value: string}> = ({name, value}) => (
  <div className="ui-kit__swatch">
    <span style={{background: value}} />
    <strong>{name}</strong>
    <small>{value}</small>
  </div>
);

const Section: FC<{
  id: string;
  index: string;
  title: string;
  description: string;
}> = ({id, index, title, description, children}) => (
  <section className="ui-kit__section" id={id}>
    <div className="ui-kit__section-heading">
      <Eyebrow>{index}</Eyebrow>
      <Heading level={2}>{title}</Heading>
      <Text tone="muted">{description}</Text>
    </div>
    {children}
  </section>
);

const UiKit: FC = () => {
  const [mode, setMode] = useState('pledge');
  const [amount, setAmount] = useState(25);
  const [noticeVisible, setNoticeVisible] = useState(true);

  return (
    <main className="ui-kit">
      <header className="ui-kit__nav">
        <a href="/" aria-label="Back to souha.it">
          <Brand />
        </a>
        <nav aria-label="UI kit sections">
          <a href="#foundations">Foundations</a>
          <a href="#actions">Actions</a>
          <a href="#identity">Identity</a>
          <a href="#forms">Forms</a>
          <a href="#product">Product</a>
        </nav>
        <Badge icon="sparkle" tone="mustard">
          UI kit
        </Badge>
      </header>

      <section className="ui-kit__hero">
        <div>
          <Eyebrow>Souha.it design system · v1</Eyebrow>
          <Heading display level={1}>
            Useful things,
            <br />
            made human.
          </Heading>
          <Text size="lg" tone="muted">
            The atoms and product primitives behind a universal wishlist that
            friends can make happen together.
          </Text>
        </div>
        <Paper className="ui-kit__hero-paper" variant="pinned">
          <Motif name="gift" size={150} />
          <Motif name="heart" size={58} />
          <Motif name="sparkle" size={46} />
          <p>Many hearts, one wish.</p>
        </Paper>
      </section>

      <Section
        description="A restrained palette, expressive type, and tactile paper surfaces."
        id="foundations"
        index="01 · Foundations"
        title="Warmth with structure"
      >
        <div className="ui-kit__swatches">
          <Swatch name="Paper" value="#F5EEDC" />
          <Swatch name="Navy" value="#17385F" />
          <Swatch name="Terracotta" value="#C95C3C" />
          <Swatch name="Mustard" value="#D8A637" />
          <Swatch name="Leaf" value="#536A45" />
          <Swatch name="Sky" value="#8FB3CF" />
          <Swatch name="Raspberry" value="#B84A58" />
        </div>
        <div className="ui-kit__type">
          <div>
            <Eyebrow>Display serif</Eyebrow>
            <Heading display level={2}>
              A wish worth sharing.
            </Heading>
          </div>
          <div>
            <Eyebrow>Humanist sans</Eyebrow>
            <Heading level={3}>Clear where it counts.</Heading>
            <Text tone="muted">
              Labels, forms, prices, contribution status, and the practical
              details that make generosity easy.
            </Text>
          </div>
        </div>
        <div className="ui-kit__papers">
          <Paper variant="flat">Flat paper</Paper>
          <Paper variant="raised">Raised paper</Paper>
          <Paper variant="pinned">Pinned note</Paper>
          <Paper variant="tinted">Tinted context</Paper>
        </div>
      </Section>

      <Divider>Make something happen</Divider>

      <Section
        description="Every action has a clear hierarchy and a tactile pressed state."
        id="actions"
        index="02 · Actions"
        title="Buttons that feel held"
      >
        <div className="ui-kit__button-grid">
          <Button iconStart="plus" size="lg">
            Add a wish
          </Button>
          <Button iconStart="heart" variant="secondary">
            Pledge together
          </Button>
          <Button iconEnd="arrow-right" variant="paper">
            View the wish
          </Button>
          <Button variant="ghost">Cancel</Button>
          <Button loading>Saving wish</Button>
          <Button disabled>Unavailable</Button>
        </div>
        <div className="ui-kit__row">
          <IconButton icon="plus" label="Add" variant="primary" />
          <IconButton icon="share" label="Share" />
          <IconButton icon="heart" label="Love" />
          <IconButton icon="menu" label="Menu" variant="ghost" />
          <Badge tone="terracotta">Almost there</Badge>
          <Badge icon="users" tone="leaf">
            6 friends
          </Badge>
          <Badge tone="sky">Choose for me</Badge>
        </div>
      </Section>

      <Section
        description="Faces, progress, and friendly feedback make pooled gifting legible."
        id="identity"
        index="03 · Identity & status"
        title="People are the proof"
      >
        <div className="ui-kit__identity">
          <Paper className="ui-kit__identity-block" variant="raised">
            <Avatar
              name="Julien"
              size="xl"
              src="/images/souhait/avatars/profile-julien.webp"
            />
            <AvatarStack avatars={avatars} max={5} />
            <Text size="sm" tone="muted">
              Five friends are making this wish happen.
            </Text>
          </Paper>
          <Paper className="ui-kit__identity-block" variant="flat">
            <ProgressRing
              label="€215 of €340"
              max={340}
              size={136}
              value={215}
            />
            <ProgressBar label="Cabin getaway" max={340} value={215} />
            <ProgressBar
              label="Design book"
              max={120}
              tone="terracotta"
              value={78}
            />
          </Paper>
          <div className="ui-kit__notices">
            {noticeVisible && (
              <Notice title="Wish saved" tone="success">
                It is now visible on your public list.
              </Notice>
            )}
            <Notice title="Nothing moves yet" tone="warm">
              A gift captain coordinates payment when the group is ready.
            </Notice>
            <Button
              onClick={() => setNoticeVisible(value => !value)}
              size="sm"
              variant="ghost"
            >
              Toggle success notice
            </Button>
            <Spinner label="Fetching product details" />
          </div>
        </div>
      </Section>

      <Section
        description="Low-friction inputs for links, stories, options, and consent."
        id="forms"
        index="04 · Forms"
        title="Capture without ceremony"
      >
        <Paper className="ui-kit__form" variant="raised">
          <Field
            hint="Paste any public product page."
            htmlFor="kit-url"
            label="Product link"
          >
            <Input
              id="kit-url"
              placeholder="https://shop.example.com/the-perfect-thing"
              type="url"
            />
          </Field>
          <div className="ui-kit__form-row">
            <Field htmlFor="kit-price" label="Price">
              <Input id="kit-price" placeholder="120" type="number" />
            </Field>
            <Field htmlFor="kit-currency" label="Currency">
              <Select
                defaultValue="EUR"
                id="kit-currency"
                options={[
                  {
                    value: 'EUR',
                    label: 'EUR · €',
                    description: 'Euro',
                  },
                  {
                    value: 'GBP',
                    label: 'GBP · £',
                    description: 'British pound',
                  },
                  {
                    value: 'USD',
                    label: 'USD · $',
                    description: 'US dollar',
                  },
                ]}
              />
            </Field>
          </div>
          <Field
            htmlFor="kit-note"
            label="Why this one?"
            optional
          >
            <Textarea
              id="kit-note"
              placeholder="A short note helps people understand why you chose it."
            />
          </Field>
          <SegmentedControl
            label="Gift mode"
            onChange={setMode}
            options={[
              {
                value: 'pledge',
                label: 'Pledge together',
                description: 'Friends contribute toward it.',
              },
              {
                value: 'gift',
                label: 'Gift this',
                description: 'One person takes care of it.',
              },
              {
                value: 'choose',
                label: 'Choose for me',
                description: 'The giver decides the final version.',
              },
            ]}
            value={mode}
          />
          <Checkbox
            defaultChecked
            description="Show contribution progress without revealing individual amounts."
            label="Keep contributions discreet"
          />
          <Button fullWidth iconStart="link">
            Fetch product details
          </Button>
        </Paper>
      </Section>

      <Section
        description="Base components composed into the recurring moments of the product."
        id="product"
        index="05 · Product components"
        title="From atoms to wishes"
      >
        <div className="ui-kit__product-grid">
          <WishCard
            contributors={avatars}
            goal={120}
            image="/images/souhait/illustrations/wish-game-ui-book.webp"
            merchant="Lost in Cult"
            note="The edition I know I will keep returning to."
            pledged={78}
            price="€120"
            status="Pre-order"
            title="The Game UI Bible"
          />
          <WishCard
            image="/images/souhait/illustrations/wish-reading-chair.webp"
            merchant="A good furniture shop"
            mode="gift"
            note="Deep blue, generous, and made for long Sunday reading."
            price="€890"
            title="A chair worth reading in"
          />
          <PledgeControl
            amount={amount}
            contributors={avatars}
            goal={120}
            onAmountChange={setAmount}
            onSubmit={() => setNoticeVisible(true)}
            pledged={78}
          />
        </div>
        <Paper className="ui-kit__empty" variant="tinted">
          <EmptyState
            actionLabel="Add the first wish"
            description="Paste a link from any shop. We will turn it into a wish you can explain and share."
            image="/images/souhait/states/empty-list.webp"
            onAction={() => undefined}
            title="This list is waiting for its first thing."
          />
        </Paper>
      </Section>

      <footer className="ui-kit__footer">
        <Brand />
        <Text size="sm" tone="muted">
          Warm, useful, and built to be shared.
        </Text>
      </footer>
    </main>
  );
};

export default UiKit;
